/**
 * @description This class handles represents the ground of the scene. It managed picking a loaded poincloud or a ground plane.
 * Also manages the rendering of the ground grid.
 */

'use strict';

import { GridHelper, Mesh, MeshPhongMaterial, Object3D, PlaneGeometry, Raycaster } from 'three';

import { PointCloudHandle } from './geodata/point-clouds/point-cloud-handle';
import { renderOrders } from './render-orders';
import { disposeRecursive } from './utilities/three/dispose';

export class Ground extends Object3D {
  static get instance() {
    return Ground._instance;
  }
  private static _instance: Ground = new Ground();

  private groundMesh: Mesh;
  private grid: GridHelper;

  constructor() {
    super();

    // Create the mesh we are picking against
    const gridGeometry = new PlaneGeometry(30000000, 30000000);
    const gridMaterial = new MeshPhongMaterial({
      color: 0x000000,
      opacity: 0.0,
      transparent: true,
      specular: 0x000000,
      depthWrite: false,
    });
    this.groundMesh = new Mesh(gridGeometry, gridMaterial);

    this.add(this.groundMesh);

    this.renderOrder = renderOrders.ground;

    // Create the grid to be rendered
    const grid = new GridHelper(200, 20, 0xfffffff, 0xcccccc);
    grid.rotation.x = Math.PI / 2;
    this.grid = grid;
  }

  /**
   * @description Declare if the grid is to be rendered.
   * @param {boolean} state
   */
  displayGrid(state: boolean) {
    const alreadyAdded = this.children.find((child) => this.grid.uuid === child.uuid);

    // If we want to display it, and it is not already rendered, add it
    // If we do not want to display it, and it is rendered, remove it
    state && !alreadyAdded && this.add(this.grid);
    !state && alreadyAdded && this.remove(this.grid);
  }

  dispose() {
    disposeRecursive(this.groundMesh);
  }

  /**
   *  Check if point cloud is available and picks against it.
   *  If it is not available, pick against the ground plane.
   */
  pick(raycaster: Raycaster) {
    let intersections;
    if (PointCloudHandle.instance.activePointCloud) {
      intersections = PointCloudHandle.instance.activePointCloud.pick(raycaster);
    } else {
      intersections = this.pickPlane(raycaster);
    }

    return intersections;
  }

  /**
   *  Pick the ground plane of the scene.
   *
   */
  pickPlane(raycaster: Raycaster) {
    return raycaster.intersectObject(this.groundMesh);
  }
}
